import React, { useState } from 'react';
import { useBrand } from '../hooks/useBrand.js';
import { BAGS_PER_PLAYER } from '../constants/index.js';
import { validateBagCount, computePlayerPoints } from '../services/scoring.js';
import type { PlayerStats } from '../types/index.js';

// ============================================================
// Constants
// ============================================================

const BAG_COLUMNS = [0, 1, 2, 3, 4] as const;
type BagCount = (typeof BAG_COLUMNS)[number];

type BagRow = 'bagsIn' | 'bagsOn' | 'bagsOff';

const BAG_ROWS: Array<{ key: BagRow; label: string }> = [
  { key: 'bagsIn', label: 'In' },
  { key: 'bagsOn', label: 'On' },
  { key: 'bagsOff', label: 'Off' },
];

// ============================================================
// Types
// ============================================================

export interface ScoreEntryIntermediateProps {
  /** Players who threw this round. Index 0 = left column, index 1 = right column. */
  players: Array<{ id: string; name: string; teamColor: string }>;
  roundNumber: number;
  /** Called with per-player stats when the user confirms the round. */
  onSubmit: (playerStats: PlayerStats[]) => void;
  /** Called when the overlay is dismissed without submitting. */
  onDismiss: () => void;
  /** Optional gear button callback — renders gear icon to the left of Cancel when provided */
  onGearTap?: () => void;
  /** Label for the submit button. Defaults to "Submit Round". Pass "Update Round" in correction mode. */
  submitLabel?: string;
}

interface PlayerPadState {
  bagsIn: BagCount;
  bagsOn: BagCount;
  bagsOff: BagCount;
}

const DEFAULT_PAD_STATE: PlayerPadState = { bagsIn: 0, bagsOn: 0, bagsOff: 0 };

// ============================================================
// ScoreEntryIntermediate
// ============================================================

/**
 * Score entry overlay — Intermediate PPR mode.
 * Teaching mode: players select Bags In / Bags On / Bags Off per player per round.
 * Total bags must equal 4 for each player before Submit enables.
 * Dismissed via Cancel or Submit only.
 *
 * Layout scaling: card is set to 80dvh (80% of viewport height) with
 * container-type: size. All internal sizing uses cqh/cqw units relative to
 * the card container, so the UI scales proportionally on any tablet size —
 * 7" through iPad Pro. Matches the scaling approach used in Scoreboard.tsx.
 *
 * Round indicator floats as an absolute badge centered over the player name
 * headers — no dedicated row cost. Player name headers fill the full top bar
 * at 10cqh height with 8cqh font (80% fill).
 */
export function ScoreEntryIntermediate({
  players,
  roundNumber,
  onSubmit,
  onDismiss,
  onGearTap,
  submitLabel = 'Submit Round',
}: ScoreEntryIntermediateProps) {
  const brand = useBrand();
  const [pads, setPads] = useState<PlayerPadState[]>(
    players.map(() => ({ ...DEFAULT_PAD_STATE })),
  );

  const handleCellTap = (playerIdx: number, row: BagRow, value: BagCount) => {
    setPads((prev) => {
      const next = [...prev];
      next[playerIdx] = { ...next[playerIdx], [row]: value };
      return next;
    });
  };

  const isPlayerValid = (pad: PlayerPadState) =>
    validateBagCount(pad.bagsIn, pad.bagsOn, pad.bagsOff);

  const isReadyToSubmit = pads.every(isPlayerValid);

  const handleSubmit = () => {
    if (!isReadyToSubmit) return;

    const stats: PlayerStats[] = pads.map((pad, i) => ({
      playerId: players[i].id,
      bagsIn: pad.bagsIn,
      bagsOn: pad.bagsOn,
      bagsOff: pad.bagsOff,
      totalPoints: computePlayerPoints(pad.bagsIn, pad.bagsOn),
    }));

    onSubmit(stats);
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.80)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  };

  /**
   * Card is the CSS size-query container.
   * Height: 80dvh (falls back to 80vh for older browsers).
   * All cqh values below are relative to THIS element's height.
   * Width: 90vw — fills 90% of viewport; no pixel cap so landscape tablets use full space.
   *
   * Approximate vertical budget (80dvh = 100cqh of this container):
   *   Padding top+bottom:   4cqh
   *   Player pads:          ~84cqh (flex:1, fills remaining — player name headers are 10cqh)
   *   Action buttons:       10cqh
   *   Gap between sections:  2cqh
   *   Total ≈ 100cqh — round badge floats over player name area, no row cost
   */
  const cardStyle: React.CSSProperties = {
    backgroundColor: brand.colors.background,
    borderRadius: '1.5cqh',
    // 80dvh = 80% of the dynamic viewport height (accounts for mobile chrome bars).
    // Supported in Chrome 108+, Firefox 110+, Safari 15.5+ — all modern tablets.
    height: '80dvh',
    width: '90vw',
    padding: '2cqh 1.5cqw',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5cqh',
    boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
    color: brand.colors.text,
    fontFamily: 'system-ui, sans-serif',
    // Makes this element a CSS container for cqh/cqw queries
    containerType: 'size',
    overflow: 'hidden',
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Enter round score">
      <div style={cardStyle}>
        {/* Player pads — flex:1 fills full height; position:relative anchors the floating round badge */}
        <div style={{ display: 'flex', gap: '1.5cqw', flex: 1, minHeight: 0, position: 'relative' }}>
          {/* Floating round badge — centered over player name headers, no layout row cost */}
          <div
            style={{
              position: 'absolute',
              top: '1.5cqh',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#000000',
              color: '#ffffff',
              borderRadius: '2cqh',
              padding: '0.4cqh 1.2cqw',
              pointerEvents: 'none',
              zIndex: 10,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: '1.8cqh', fontWeight: 500, letterSpacing: '0.08em', opacity: 0.85, lineHeight: 1.2, textTransform: 'uppercase' }}>
              ROUND
            </div>
            <div style={{ fontSize: '4.5cqh', fontWeight: 900, lineHeight: 1 }}>
              {roundNumber}
            </div>
          </div>

          {players.map((player, idx) => (
            <PlayerBagPad
              key={player.id}
              player={player}
              pad={pads[idx]}
              isValid={isPlayerValid(pads[idx])}
              textColor={brand.colors.text}
              onCellTap={(row, value) => handleCellTap(idx, row, value)}
            />
          ))}
        </div>

        {/* Action buttons — fixed-ish height via cqh padding */}
        <div style={{ display: 'flex', gap: '1cqw', flexShrink: 0 }}>
          {onGearTap && (
            <button
              onClick={onGearTap}
              aria-label="Game settings"
              style={{
                flexShrink: 0,
                width: '8cqh',
                height: '8cqh',
                padding: 0,
                fontSize: '3.5cqh',
                border: `2px solid ${brand.colors.text}`,
                borderRadius: '1cqh',
                background: 'transparent',
                color: brand.colors.text,
                cursor: 'pointer',
                touchAction: 'manipulation',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ⚙️
            </button>
          )}
          <button
            onClick={onDismiss}
            style={{
              flex: 1,
              padding: '2.5cqh 0',
              fontSize: '2.5cqh',
              border: `2px solid ${brand.colors.text}`,
              borderRadius: '1cqh',
              background: 'transparent',
              color: brand.colors.text,
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isReadyToSubmit}
            style={{
              flex: 2,
              padding: '2.5cqh 0',
              fontSize: '3cqh',
              fontWeight: 700,
              border: 'none',
              borderRadius: '1cqh',
              background: isReadyToSubmit ? brand.colors.primary : 'rgba(255,255,255,0.15)',
              color: isReadyToSubmit ? '#ffffff' : 'rgba(255,255,255,0.4)',
              cursor: isReadyToSubmit ? 'pointer' : 'not-allowed',
              touchAction: 'manipulation',
              transition: 'background 0.15s',
            }}
            aria-disabled={!isReadyToSubmit}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PlayerBagPad — internal
// ============================================================

interface PlayerBagPadProps {
  player: { id: string; name: string; teamColor: string };
  pad: PlayerPadState;
  isValid: boolean;
  textColor: string;
  onCellTap: (row: BagRow, value: BagCount) => void;
}

function PlayerBagPad({ player, pad, isValid, textColor, onCellTap }: PlayerBagPadProps) {
  const totalBags = pad.bagsIn + pad.bagsOn + pad.bagsOff;
  const totalPoints = computePlayerPoints(pad.bagsIn, pad.bagsOn);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1cqh', minHeight: 0 }}>
      {/* Player name header — 10cqh tall; font 8cqh = 80% of header height, fills the bar */}
      <div
        style={{
          height: '10cqh',
          fontSize: '8cqh',
          fontWeight: 700,
          textAlign: 'center',
          borderRadius: '0.8cqh',
          background: player.teamColor,
          color: '#ffffff',
          flexShrink: 0,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {player.name}
      </div>

      {/* Bag entry grid — flex:1 fills remaining column height */}
      <div
        style={{
          border: isValid
            ? `2px solid ${player.teamColor}`
            : '2px solid rgba(255,255,255,0.15)',
          borderRadius: '1cqh',
          overflow: 'hidden',
          transition: 'border-color 0.15s',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        role="group"
        aria-label={`Bag entry for ${player.name}`}
      >
        {/* Column headers (0–4 labels) */}
        <div
          style={{
            display: 'grid',
            // Label column: 15cqw ≈ 15% of card width; 5 equal value columns
            gridTemplateColumns: '15cqw repeat(5, 1fr)',
            background: 'rgba(255,255,255,0.08)',
            flexShrink: 0,
          }}
        >
          <div style={{ padding: '0.8cqh 0', fontSize: '2cqh', fontWeight: 700, textAlign: 'center', opacity: 0.85, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Bags</div>
          {BAG_COLUMNS.map((n) => (
            <div
              key={n}
              style={{
                padding: '0.8cqh 0',
                fontSize: '2.5cqh',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              {n}
            </div>
          ))}
        </div>

        {/* Row per bag type — each flex:1 to fill remaining grid height */}
        {BAG_ROWS.map(({ key, label }) => (
          <BagRow
            key={key}
            label={label}
            rowKey={key}
            selectedValue={pad[key]}
            teamColor={player.teamColor}
            textColor={textColor}
            onTap={onCellTap}
          />
        ))}
      </div>

      {/* Status: bags counted + points — 2.5cqh font */}
      <div
        style={{
          fontSize: '2.5cqh',
          textAlign: 'center',
          padding: '0.5cqh 0',
          flexShrink: 0,
        }}
      >
        <span style={{ opacity: 0.75 }}>{totalBags} / {BAGS_PER_PLAYER} bags</span>
        {isValid && <span style={{ color: '#22c55e', fontWeight: 600 }}>{` · ${totalPoints} pts ✓`}</span>}
        {!isValid && totalBags > BAGS_PER_PLAYER && <span style={{ color: '#ef4444', fontWeight: 600 }}>{' · Too many!'}</span>}
      </div>
    </div>
  );
}

// ============================================================
// BagRow — internal
// ============================================================

interface BagRowProps {
  label: string;
  rowKey: BagRow;
  selectedValue: BagCount;
  teamColor: string;
  textColor: string;
  onTap: (row: BagRow, value: BagCount) => void;
}

function BagRow({ label, rowKey, selectedValue, teamColor, textColor, onTap }: BagRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        // Matches the header column widths: 15cqw label + 5 equal value columns
        gridTemplateColumns: '15cqw repeat(5, 1fr)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* Row label — scales with container */}
      <div
        style={{
          padding: '0 0.5cqw',
          fontSize: '8cqh',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          opacity: 0.85,
          background: 'rgba(255,255,255,0.04)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          lineHeight: 1.1,
        }}
      >
        {label}
      </div>

      {BAG_COLUMNS.map((n) => {
        const isSelected = selectedValue === n;
        return (
          <button
            key={n}
            onClick={() => onTap(rowKey, n as BagCount)}
            style={{
              // Height fills the row (flex:1 on parent), font scales with cqh
              width: '100%',
              height: '100%',
              fontSize: '13cqh',
              fontWeight: 700,
              border: 'none',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '1.2cqh',
              background: isSelected ? teamColor : 'transparent',
              color: isSelected ? '#ffffff' : textColor,
              cursor: 'pointer',
              touchAction: 'manipulation',
              transition: 'background 0.1s',
            }}
            aria-pressed={isSelected}
            aria-label={`${label}: ${n}`}
          >
            {isSelected ? n : <span style={{ opacity: 0.45 }}>{n}</span>}
          </button>
        );
      })}
    </div>
  );
}
